import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import styles from './Button.module.css';

type Variant = 'primary' | 'cta' | 'onInverse' | 'text' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

type CommonProps = {
  variant?: Variant;
  size?: Size;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  loading?: boolean;
  children?: ReactNode;
  className?: string;
};

type ButtonAsButton = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof CommonProps> & {
    as?: 'button';
  };

type ButtonAsAnchor = CommonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof CommonProps> & {
    as: 'a';
    href: string;
  };

type ButtonAsLink = CommonProps &
  Omit<LinkProps, keyof CommonProps> & {
    as: 'link';
    to: LinkProps['to'];
  };

type ButtonProps = ButtonAsButton | ButtonAsAnchor | ButtonAsLink;

function classNames(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

const Button = forwardRef<HTMLElement, ButtonProps>(function Button(props, ref) {
  const {
    variant = 'primary',
    size = 'md',
    iconLeft,
    iconRight,
    loading,
    children,
    className,
    ...rest
  } = props as CommonProps & Record<string, unknown>;

  const cls = classNames(
    styles.btn,
    styles[`variant-${variant}`],
    styles[`size-${size}`],
    loading && styles.loading,
    className,
  );

  const inner = (
    <>
      {iconLeft && <span className={styles.icon}>{iconLeft}</span>}
      {children && <span className={styles.label}>{children}</span>}
      {iconRight && <span className={styles.icon}>{iconRight}</span>}
      {variant === 'primary' || variant === 'cta' || variant === 'onInverse' ? (
        <span className={styles.arrow} aria-hidden="true" />
      ) : null}
    </>
  );

  if ((props as ButtonAsAnchor).as === 'a') {
    const { as: _as, ...anchorRest } = rest as AnchorHTMLAttributes<HTMLAnchorElement> & {
      as?: string;
    };
    return (
      <a ref={ref as React.Ref<HTMLAnchorElement>} className={cls} {...anchorRest}>
        {inner}
      </a>
    );
  }
  if ((props as ButtonAsLink).as === 'link') {
    const { as: _as, ...linkRest } = rest as unknown as LinkProps & { as?: string };
    return (
      <Link
        ref={ref as React.Ref<HTMLAnchorElement>}
        className={cls}
        {...(linkRest as LinkProps)}
      >
        {inner}
      </Link>
    );
  }
  const {
    as: _as,
    disabled: disabledProp,
    ...buttonRest
  } = rest as ButtonHTMLAttributes<HTMLButtonElement> & {
    as?: string;
  };
  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      type="button"
      className={cls}
      {...buttonRest}
      // disabled は spread の後で確定させる（loading 中に disabled={false} で上書きされないよう）
      disabled={loading || disabledProp}
    >
      {inner}
    </button>
  );
});

export default Button;
